import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { COLOR_MAP } from '../../constants/ColorNames';

type ImageCropPropsType = {
  media: FileList | string;
  onSubmit: (src: string) => void;
  height: number;
  width: number;
};

const PreviewImage = styled.img`
  height: 100% !important;
  max-height: 42.8rem !important;
  width: 100% !important;
  max-width: 66.2rem !important;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-width: 682px;
  min-height: 228px;
  background-color: ${COLOR_MAP.GREY_0};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageCrop: React.FC<ImageCropPropsType> = (props) => {
  const { media, onSubmit, height, width } = props;
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop | undefined>();

  const previewImageRef = React.useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (media && media.length > 0 && typeof media !== 'string') {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(media[0]);
    } else if (typeof media === 'string') {
      setImgSrc(media);
    }
  }, [media]);


  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
   
    if (e.currentTarget.width && e.currentTarget.height) {
      const mediaWidth = e.currentTarget.width;
      const mediaHeight = e.currentTarget.height;
      const desiredWidth = width; // Adjust this as needed
      const desiredHeight = height; // Adjust this as needed

      // Calculate initial crop position to center it
      const x = Math.max(0, (mediaWidth - desiredWidth) / 2);
      const y = Math.max(0, (mediaHeight - desiredHeight) / 2);

      // Adjust crop size if the image is smaller than the desired crop size
      const cropWidth = Math.min(desiredWidth, mediaWidth);
      const cropHeight = Math.min(desiredHeight, mediaHeight);

      const crop : Crop = {
        unit: 'px',
        width: cropWidth,
        height: cropHeight,
        x,
        y,
      }

      setCrop(crop);
      makeClientCrop(crop);
    }
  }

  const makeClientCrop = (crop: Crop) => {
    if (imgSrc && crop.width && crop.height) {
      const image = new Image();
      image.src = imgSrc;
  
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');
  
        // Check if previewImageRef.current is not null
        if (ctx && previewImageRef.current) {
          // Calculate the source image dimensions
          const imageWidth = image.width;
          const imageHeight = image.height;
  
          // Calculate the scale factor for the source image
          const scaleFactorX = imageWidth / previewImageRef.current.width;
          const scaleFactorY = imageHeight / previewImageRef.current.height;
  
          // Calculate the source coordinates based on the crop position
          const sourceX = crop.x * scaleFactorX;
          const sourceY = crop.y * scaleFactorY;
          const sourceWidth = crop.width * scaleFactorX;
          const sourceHeight = crop.height * scaleFactorY;
  
          // Draw the cropped portion of the image onto the canvas
          ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            crop.width,
            crop.height
          );
  
          const croppedImageBase64 = canvas.toDataURL('image/jpeg');
          onSubmit(croppedImageBase64);
        }
      };
    }
  };
  
  
  

  return (
    <ImageWrapper>
      <ReactCrop
        crop={crop}
        onChange={(c) => setCrop(c)}
        onComplete={(c) => makeClientCrop(c)}
        locked={true}
      >
        <PreviewImage ref={previewImageRef} alt="cover" onLoad={onImageLoad} src={imgSrc} />
      </ReactCrop>
    </ImageWrapper>
  );
};

export default ImageCrop;
